import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { bautizosAPI } from "@/lib/api";
import { ArrowLeft, Edit, Printer, Droplets, Calendar, MapPin, User, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";

export default function BautizoDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [bautizo, setBautizo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBautizo();
  }, [id]);

  const loadBautizo = async () => {
    try {
      const response = await bautizosAPI.getOne(id);
      setBautizo(response.data);
    } catch (error) {
      toast.error("Error al cargar el bautizo");
      navigate("/bautizos");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-stone-400">Cargando...</div>
      </div>
    );
  }

  if (!bautizo) return null;

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="bautizo-detail-page">
      {/* Header - No print */}
      <div className="flex items-center justify-between no-print">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/bautizos")}
            className="h-10 w-10"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-serif text-rose-950 tracking-tight flex items-center gap-3">
              <Droplets className="w-8 h-8 text-blue-600" strokeWidth={1.5} />
              Acta de Bautizo
            </h1>
            <p className="text-stone-600 mt-1">Partida #{bautizo.numero_partida}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to={`/bautizos/${id}/editar`}>
            <Button variant="outline" className="h-10">
              <Edit className="w-4 h-4 mr-2" />
              Editar
            </Button>
          </Link>
          <Button onClick={handlePrint} className="h-10 bg-rose-950 hover:bg-rose-900 text-white">
            <Printer className="w-4 h-4 mr-2" />
            Imprimir Acta
          </Button>
        </div>
      </div>

      {/* Print Certificate */}
      <div className="print-certificate">
        {/* Print Header */}
        <div className="print-header hidden print:block text-center mb-8">
          <h1 className="text-3xl font-serif text-rose-950">ACTA DE BAUTISMO</h1>
          <p className="text-stone-600 mt-2">Parroquia San Juan Bautista</p>
          <p className="text-stone-500 text-sm">Diócesis de San Juan</p>
        </div>

        {/* Datos del Registro */}
        <Card className="border-stone-200 bg-white mb-6">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-medium text-stone-800">
                Datos del Registro
              </CardTitle>
              <Badge variant="outline" className="text-blue-600 border-blue-200 bg-blue-50">
                Libro {bautizo.numero_libro} · Folio {bautizo.numero_folio} · Partida {bautizo.numero_partida}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-stone-400 mt-0.5" />
              <div>
                <p className="text-xs text-stone-500 uppercase tracking-wide">Fecha</p>
                <p className="text-stone-800 font-medium">{bautizo.fecha_bautizo}</p>
              </div>
            </div>
            <div className="flex items-start gap-3 md:col-span-3">
              <MapPin className="w-5 h-5 text-stone-400 mt-0.5" />
              <div>
                <p className="text-xs text-stone-500 uppercase tracking-wide">Lugar</p>
                <p className="text-stone-800 font-medium">{bautizo.lugar_bautizo}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Datos del Bautizado */}
        <Card className="border-stone-200 bg-white mb-6">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-medium text-stone-800 flex items-center gap-2">
              <User className="w-5 h-5 text-blue-600" />
              Bautizado
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-xs text-stone-500 uppercase tracking-wide">Nombre Completo</p>
              <p className="text-xl font-serif text-stone-800 mt-1">
                {bautizo.bautizado_nombre} {bautizo.bautizado_apellido}
              </p>
            </div>
            <div>
              <p className="text-xs text-stone-500 uppercase tracking-wide">Género</p>
              <p className="text-stone-800 mt-1 capitalize">{bautizo.bautizado_genero}</p>
            </div>
            <div>
              <p className="text-xs text-stone-500 uppercase tracking-wide">Fecha de Nacimiento</p>
              <p className="text-stone-800 mt-1">{bautizo.bautizado_fecha_nacimiento}</p>
            </div>
            <div>
              <p className="text-xs text-stone-500 uppercase tracking-wide">Lugar de Nacimiento</p>
              <p className="text-stone-800 mt-1">{bautizo.bautizado_lugar_nacimiento}</p>
            </div>
          </CardContent>
        </Card>

        {/* Padres y Padrinos */}
        <Card className="border-stone-200 bg-white mb-6">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-medium text-stone-800 flex items-center gap-2">
              <Users className="w-5 h-5 text-emerald-600" />
              Padres y Padrinos
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-xs text-stone-500 uppercase tracking-wide">Padre</p>
              <p className="text-stone-800 mt-1">{bautizo.padre_nombre || "—"}</p>
            </div>
            <div>
              <p className="text-xs text-stone-500 uppercase tracking-wide">Madre</p>
              <p className="text-stone-800 mt-1">{bautizo.madre_nombre || "—"}</p>
            </div>
            <Separator className="md:col-span-2" />
            <div>
              <p className="text-xs text-stone-500 uppercase tracking-wide">Padrino</p>
              <p className="text-stone-800 mt-1">{bautizo.padrino_nombre || "—"}</p>
            </div>
            <div>
              <p className="text-xs text-stone-500 uppercase tracking-wide">Madrina</p>
              <p className="text-stone-800 mt-1">{bautizo.madrina_nombre || "—"}</p>
            </div>
          </CardContent>
        </Card>

        {/* Ministro */}
        <Card className="border-stone-200 bg-white mb-6">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-medium text-stone-800">
              Ministro Celebrante
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-stone-800 font-medium">{bautizo.ministro_nombre}</p>
          </CardContent>
        </Card>

        {/* Notas */}
        {bautizo.notas_marginales && (
          <Card className="border-stone-200 bg-white">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-medium text-stone-800">
                Notas Marginales
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-stone-600 whitespace-pre-wrap">{bautizo.notas_marginales}</p>
            </CardContent>
          </Card>
        )}

        {/* Print Signature Area */}
        <div className="print-signature hidden print:flex justify-between mt-16 pt-8">
          <div className="text-center">
            <div className="w-48 border-t border-stone-400 pt-2">
              <p className="text-sm text-stone-600">Firma del Párroco</p>
            </div>
          </div>
          <div className="text-center">
            <div className="w-48 border-t border-stone-400 pt-2">
              <p className="text-sm text-stone-600">Sello Parroquial</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
